import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { BarChart3, ClipboardCheck, FileSpreadsheet, Plus } from "lucide-react";
import type { Survey, Session } from "@event-planner/shared";
import { PageHeader } from "../../components/PageHeader";
import { DataTable, type Column } from "../../components/DataTable";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { ExcelSurveyImportDialog } from "./ExcelSurveyImportDialog";
import { db } from "../../lib/firebase";
import { useFirestoreCollectionOnce } from "../../hooks/useFirestoreCollectionOnce";
import { useCloseSurvey, useReopenSurvey, useDeleteSurvey } from "../../hooks/useSurveyMutations";
import { getSurveyResults } from "../../services/surveyService";
import { useEvent } from "../../layouts/EventLayout";
import { PhoneHeaderActions } from "../../components/phone/PhoneHeaderActions";
import type { OverflowAction } from "../../components/phone/OverflowMenu";
import { useIsPhone } from "../../hooks/useIsPhone";
import { useSnackbar } from "../../context/SnackbarContext";

const STATUS_COLORS: Record<string, "default" | "success" | "warning" | "info"> = {
  draft: "default",
  scheduled: "info",
  open: "success",
  closed: "warning",
};


export function AnalyticsPage() {
  const isPhone = useIsPhone();
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { event } = useEvent();
  const [tab, setTab] = useState(0);
  const [importOpen, setImportOpen] = useState(false);

  // ── Surveys query ──
  const surveysQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, "surveys"),
            where("eventId", "==", eventId),
            orderBy("createdAt", "desc"),
          )
        : null,
    [eventId],
  );
  const {
    data: surveys,
    loading: surveysLoading,
    error: surveysError,
    refetch,
  } = useFirestoreCollectionOnce<Survey>(surveysQuery);

  // ── Sessions for display names ──
  const sessionsQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, "sessions"),
            where("eventId", "==", eventId),
            orderBy("startTime", "asc"),
          )
        : null,
    [eventId],
  );
  const { data: sessions, error: sessionsError } =
    useFirestoreCollectionOnce<Session>(sessionsQuery);
  const sessionMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of sessions) map.set(s.id, s.title);
    return map;
  }, [sessions]);

  // ── Response counts (per-survey, via callable) ──
  // Aggregates must come through `getSurveyResults` — direct reads on
  // `surveyResponses` are denied to organizers (ADR-054 §3, ADR-067).
  // Fires one callable per survey in parallel. At MVP scale (<20 surveys
  // per event) this is fine; if lists grow large, add a batch callable.
  const [responseCounts, setResponseCounts] = useState<Map<string, number>>(
    new Map(),
  );
  const [countsLoading, setCountsLoading] = useState(false);

  useEffect(() => {
    if (surveys.length === 0) {
      setResponseCounts(new Map());
      return;
    }
    let cancelled = false;
    setCountsLoading(true);
    Promise.all(
      surveys.map(async (s) => {
        try {
          const r = await getSurveyResults(s.id);
          return [s.id, r.totalRespondents] as const;
        } catch {
          return [s.id, 0] as const;
        }
      }),
    )
      .then((entries) => {
        if (cancelled) return;
        setResponseCounts(new Map(entries));
      })
      .finally(() => {
        if (!cancelled) setCountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [surveys]);

  // ── Mutations ──
  const { close, loading: closing } = useCloseSurvey();
  const { reopen, loading: reopening } = useReopenSurvey();
  const { remove, loading: deleting } = useDeleteSurvey();
  const [deleteTarget, setDeleteTarget] = useState<Survey | null>(null);

  async function handleClose(survey: Survey) {
    try {
      await close(survey.id);
      showSuccess("Survey closed");
      refetch();
    } catch {
      showError("Failed to close survey");
    }
  }

  async function handleReopen(survey: Survey) {
    try {
      await reopen(survey.id);
      showSuccess("Survey reopened");
      refetch();
    } catch {
      showError("Failed to reopen survey");
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await remove(deleteTarget.id);
      showSuccess("Survey deleted");
      setDeleteTarget(null);
      refetch();
    } catch {
      showError("Failed to delete survey");
    }
  }

  // ── Table columns ──
  const columns: Column<Survey>[] = useMemo(
    () => [
      {
        id: "question",
        label: "Question",
        render: (s) => (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {s.questions[0]?.prompt ?? "(no question)"}
          </Typography>
        ),
      },
      {
        id: "questionCount",
        label: "Questions",
        width: "110px",
        render: (s) => `${s.questions.length}`,
      },
      {
        id: "scope",
        label: "Scope",
        width: "180px",
        render: (s) =>
          s.sessionId
            ? sessionMap.get(s.sessionId) ?? "Session"
            : "Event-wide",
      },
      {
        id: "responses",
        label: "Responses",
        width: "110px",
        render: (s) => {
          if (countsLoading && !responseCounts.has(s.id)) return "...";
          return responseCounts.get(s.id) ?? 0;
        },
      },
      {
        id: "status",
        label: "Status",
        width: "100px",
        render: (s) => (
          <Chip
            label={s.status}
            size="small"
            color={STATUS_COLORS[s.status] ?? "default"}
          />
        ),
      },
      {
        id: "actions",
        label: "",
        width: "180px",
        align: "right" as const,
        render: (s) => (
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            {s.status === "open" && (
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose(s);
                }}
                disabled={closing}
              >
                Close
              </Button>
            )}
            {s.status === "closed" && (
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReopen(s);
                }}
                disabled={reopening}
              >
                Reopen
              </Button>
            )}
            {s.status === "draft" && (
              <Button
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget(s);
                }}
              >
                Delete
              </Button>
            )}
          </Box>
        ),
      },
    ],
    [sessionMap, closing, reopening, responseCounts, countsLoading],
  );

  // Phone: question first, then scope and a plain-words count line, the
  // status chip, and close/reopen/delete in the row menu.
  function responsesLabel(s: Survey): string {
    if (countsLoading && !responseCounts.has(s.id)) return "…";
    const n = responseCounts.get(s.id) ?? 0;
    return `${n} response${n === 1 ? "" : "s"}`;
  }
  const phoneColumns: Column<Survey>[] = [
    { ...columns[0], phone: "primary" },
    { ...columns.find((c) => c.id === "scope")!, phone: "secondary" },
    {
      id: "counts",
      label: "Counts",
      phone: "secondary",
      render: (s) => {
        const q = s.questions.length;
        return `${q} question${q === 1 ? "" : "s"}, ${responsesLabel(s)}`;
      },
    },
    { ...columns.find((c) => c.id === "status")!, phone: "trailing" },
  ];
  function surveyRowActions(s: Survey): OverflowAction[] {
    if (s.status === "open") {
      return [{ label: "Close survey", onClick: () => handleClose(s), disabled: closing }];
    }
    if (s.status === "closed") {
      return [{ label: "Reopen", onClick: () => handleReopen(s), disabled: reopening }];
    }
    if (s.status === "draft") {
      return [{ label: "Delete", onClick: () => setDeleteTarget(s), destructive: true }];
    }
    return [];
  }

  return (
    <Box>
      <PageHeader
        title="Analytics"
        action={
          isPhone ? (
            <PhoneHeaderActions
              primary={
                <Button
                  variant="contained"
                  startIcon={<Plus size={18} />}
                  onClick={() => navigate("surveys/new")}
                >
                  Create Survey
                </Button>
              }
              more={[
                {
                  label: "Import from Excel",
                  icon: <FileSpreadsheet size={18} />,
                  onClick: () => setImportOpen(true),
                  testId: "import-survey-button",
                },
              ]}
            />
          ) : (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FileSpreadsheet size={18} />}
              onClick={() => setImportOpen(true)}
              data-testid="import-survey-button"
            >
              Import from Excel
            </Button>
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={() => navigate("surveys/new")}
            >
              Create Survey
            </Button>
          </Box>
          )
        }
      />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab
          label="Surveys"
          icon={<BarChart3 size={18} />}
          iconPosition="start"
        />
        <Tab
          label="Check-in"
          icon={<ClipboardCheck size={18} />}
          iconPosition="start"
        />
      </Tabs>

      {tab === 0 && (
        <DataTable
          columns={isPhone ? phoneColumns : columns}
          data={surveys}
          loading={surveysLoading}
          error={surveysError ?? sessionsError}
          ariaLabel="Surveys for this event"
          getRowKey={(s) => s.id}
          onRowClick={(s) => navigate(`surveys/${s.id}`)}
          phoneRowActions={surveyRowActions}
          phoneRowActionsLabel={(s) => `More actions for ${s.questions[0]?.prompt ?? "survey"}`}
        />
      )}

      {tab === 1 && (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <ClipboardCheck size={48} strokeWidth={1} />
          <Typography variant="h6" sx={{ mt: 2, color: "text.secondary" }}>
            Check-in Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Coming soon — attendance tracking and check-in analytics.
          </Typography>
        </Box>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Survey"
        message={
          deleteTarget
            ? `Delete "${deleteTarget.questions[0]?.prompt ?? "this survey"}"? This also removes all responses. This cannot be undone.`
            : ""
        }
        loading={deleting}
      />

      {eventId && (
        <ExcelSurveyImportDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          eventId={eventId}
          timezone={event?.timezone ?? "UTC"}
          onImported={() => refetch()}
        />
      )}
    </Box>
  );
}
